/**
 * Brother PEC section (used inside PES files, and on its own as .pec).
 *
 * Header (512 bytes from "LA:"): label, icon geometry (6 bytes × 38 rows), colour count − 1,
 * PEC palette indices, padding. Stitch block: 0x00 0x00, u24 block length, 0x31 0xFF 0xF0,
 * width, height (0.1 mm), 0x1E0, 0x1B0, then stitch data:
 *   short form: 1 byte per axis, 7-bit signed (−64 < v < 63)
 *   long form:  2 bytes per axis, 0x80 | flags | 12-bit signed value (±2047)
 *               flags: 0x10 = jump, 0x20 = trim (jump with thread cut)
 *   0xFE 0xB0 xx = colour change (xx alternates 2, 1), 0xFF = end.
 * Followed by 1-bit 48×38 icons: one for the whole design, one per colour block.
 *
 * Reference: EmbroidePy/pyembroidery (MIT) PecWriter/PecReader; independent implementation.
 */
import { t } from '../i18n/i18n';
import { CMD } from '../engine/stitch/sequence';
import { ByteReader, ByteWriter, FormatError } from './bytes';
import { asciiName } from './dst';
import { type FormatLimits, type MachinePattern, type MachineStitch, colorBlocks, extents, type Extents } from './machine';
import { PEC_THREADS, hexToRgb, uniquePaletteIndices } from './threads';

export const PEC_LIMITS: FormatLimits = { maxStitch: 2047, maxJump: 2047, maxCoord: 32767 };

const JUMP_CODE = 0x10;
const TRIM_CODE = 0x20;
const ICON_W = 48;
const ICON_H = 38;
const ICON_STRIDE = ICON_W / 8;

/** Blank icon with a rounded frame, as expected by Brother machines. */
export function blankIcon(): number[] {
  const rows: number[][] = [];
  rows.push([0, 0, 0, 0, 0, 0]);
  rows.push([0xf0, 0xff, 0xff, 0xff, 0xff, 0x0f]);
  rows.push([0x08, 0, 0, 0, 0, 0x10]);
  rows.push([0x04, 0, 0, 0, 0, 0x20]);
  for (let i = 0; i < 30; i++) rows.push([0x02, 0, 0, 0, 0, 0x40]);
  rows.push([0x04, 0, 0, 0, 0, 0x20]);
  rows.push([0x08, 0, 0, 0, 0, 0x10]);
  rows.push([0xf0, 0xff, 0xff, 0xff, 0xff, 0x0f]);
  rows.push([0, 0, 0, 0, 0, 0]);
  return rows.flat();
}

function drawScaled(e: Extents, points: { x: number; y: number }[], icon: number[], buffer: number): void {
  const dw = e.maxX - e.minX || 1;
  const dh = e.maxY - e.minY || 1;
  const s = Math.min((ICON_W - buffer) / dw, (ICON_H - buffer) / dh);
  const cx = (e.maxX + e.minX) / 2;
  const cy = (e.maxY + e.minY) / 2;
  const tx = -cx * s + ICON_W / 2;
  const ty = -cy * s + ICON_H / 2;
  for (const p of points) {
    const x = Math.floor(p.x * s + tx);
    const y = Math.floor(p.y * s + ty);
    if (x < 0 || y < 0 || x >= ICON_W || y >= ICON_H) continue;
    icon[y * ICON_STRIDE + (x >> 3)] |= 1 << (x % 8);
  }
}

function pecValue(v: number, long: boolean, flag = 0): number[] {
  if (!long && v > -64 && v < 63) return [v & 0x7f];
  if (v < -2048 || v > 2047) throw new FormatError(t('PEC displacement outside ±2047: {v}', { v }));
  let x = v & 0x0fff;
  x |= 0x8000;
  x |= flag << 8;
  return [(x >> 8) & 0xff, x & 0xff];
}

/** Encodes the stitch commands of a PEC block. */
export function encodePecStitches(stitches: MachineStitch[], w: ByteWriter): void {
  let cx = 0, cy = 0;
  let jumping = true;
  let first = true;
  let trimPending = false;
  let colorTwo = true;
  for (const s of stitches) {
    const dx = s.x - cx;
    const dy = s.y - cy;
    switch (s.cmd) {
      case CMD.STITCH:
        if (jumping) {
          if (dx !== 0 && dy !== 0) w.bytes([0, 0]);
          jumping = false;
        }
        w.bytes(pecValue(dx, false)).bytes(pecValue(dy, false));
        cx = s.x;
        cy = s.y;
        break;
      case CMD.JUMP: {
        jumping = true;
        const flag = !first && trimPending ? TRIM_CODE : JUMP_CODE;
        w.bytes(pecValue(dx, true, flag)).bytes(pecValue(dy, true, flag));
        trimPending = false;
        cx = s.x;
        cy = s.y;
        break;
      }
      case CMD.TRIM:
        trimPending = true;
        break;
      case CMD.COLOR_CHANGE:
        if (jumping) {
          w.bytes([0, 0]);
          jumping = false;
        }
        w.bytes([0xfe, 0xb0, colorTwo ? 2 : 1]);
        colorTwo = !colorTwo;
        break;
      case CMD.STOP:
        throw new FormatError(t('PEC/PES does not support stop commands without a colour change.'));
      case CMD.END:
        break;
    }
    first = false;
  }
  w.u8(0xff);
}

/** Writes the PEC section (starting at "LA:") and returns the palette indices used. */
export function writePecSection(p: MachinePattern, w: ByteWriter): number[] {
  const blocks = colorBlocks(p.stitches).filter((b) => b.length > 0);
  const threadCount = Math.max(p.threads.length, blocks.filter((b) => b.some((s) => s.cmd === CMD.STITCH)).length);
  const rgbs = Array.from({ length: threadCount }, (_, i) => hexToRgb(p.threads[i]?.hex ?? '#000000'));
  const indices = uniquePaletteIndices(rgbs, PEC_THREADS);

  const name = asciiName(p.name, 8);
  w.ascii('LA:' + name.padEnd(16, ' ') + '\r');
  w.fill(0x20, 12).u8(0xff).u8(0x00);
  w.u8(ICON_STRIDE).u8(ICON_H);
  if (indices.length) {
    w.fill(0x20, 12);
    if (indices.length - 1 >= 255) throw new FormatError(t('Too many colour changes for PEC (max 255).'));
    w.u8(indices.length - 1);
    w.bytes(indices);
  } else {
    w.bytes([0x20, 0x20, 0x20, 0x20, 0x64, 0x20, 0x00, 0x20, 0x00, 0x20, 0x20, 0x20, 0xff]);
  }
  w.fill(0x20, 463 - indices.length);

  const e = extents(p.stitches);
  const start = w.pos;
  w.bytes([0, 0]);
  w.u24le(0);
  w.bytes([0x31, 0xff, 0xf0]);
  w.u16le(e.maxX - e.minX).u16le(e.maxY - e.minY);
  w.u16le(0x1e0).u16le(0x1b0);
  encodePecStitches(p.stitches, w);
  const blockLength = w.pos - start;
  w.patch(start + 2, (x) => x.u24le(blockLength));

  // icons: whole design (stitch runs only), then one per colour block
  const all = blankIcon();
  drawScaled(e, p.stitches.filter((s) => s.cmd === CMD.STITCH), all, 4);
  w.bytes(all);
  const graphicBlocks = colorBlocksForIcons(p.stitches);
  for (const b of graphicBlocks) {
    const icon = blankIcon();
    drawScaled(e, b.filter((s) => s.cmd === CMD.STITCH), icon, 5);
    w.bytes(icon);
  }
  return indices;
}

/** Colour blocks where the COLOR_CHANGE command ends a block (icon convention). */
function colorBlocksForIcons(stitches: MachineStitch[]): MachineStitch[][] {
  const out: MachineStitch[][] = [];
  let cur: MachineStitch[] = [];
  for (const s of stitches) {
    cur.push(s);
    if (s.cmd === CMD.COLOR_CHANGE) {
      out.push(cur);
      cur = [];
    }
  }
  out.push(cur);
  return out;
}

export function writePec(p: MachinePattern): Uint8Array {
  const w = new ByteWriter();
  w.ascii('#PEC0001');
  writePecSection(p, w);
  return w.toBytes();
}

export interface PecSection {
  label: string;
  colorIndices: number[];
  width: number;
  height: number;
  stitches: MachineStitch[];
  /** Byte offset after the stitch block (start of icons). */
  graphicsOffset: number;
  iconCount: number;
}

const signed12 = (v: number): number => {
  const x = v & 0xfff;
  return x > 0x7ff ? x - 0x1000 : x;
};
const signed7 = (v: number): number => (v > 63 ? v - 128 : v);

/** Parses a PEC section starting at `offset` (pointing at "LA:"). */
export function readPecSection(bytes: Uint8Array, offset: number): PecSection {
  const r = new ByteReader(bytes);
  r.seek(offset);
  if (r.ascii(3) !== 'LA:') throw new FormatError(t('The PEC section is missing "LA:".'));
  const label = r.ascii(16).trim();
  r.skip(0xf);
  const stride = r.u8();
  const iconH = r.u8();
  if (stride !== ICON_STRIDE || iconH !== ICON_H) throw new FormatError(t('Unexpected PEC icon format.'));
  r.skip(0xc);
  const changes = r.u8();
  const colorIndices: number[] = [];
  for (let i = 0; i <= changes; i++) colorIndices.push(r.u8());
  r.seek(offset + 512);
  const blockStart = r.pos;
  if (r.u8() !== 0 || r.u8() !== 0) throw new FormatError(t('Invalid start of the PEC stitch block.'));
  const blockLength = r.u24le();
  const magic = [r.u8(), r.u8(), r.u8()];
  if (magic[0] !== 0x31 || magic[1] !== 0xff || magic[2] !== 0xf0) throw new FormatError(t('Invalid PEC stitch block signature.'));
  const width = r.u16le();
  const height = r.u16le();
  r.skip(4);
  const stitches: MachineStitch[] = [];
  let x = 0, y = 0;
  let ended = false;
  while (r.pos < blockStart + blockLength) {
    const v1 = r.u8();
    if (v1 === 0xff) {
      ended = true;
      break;
    }
    const v2 = r.u8();
    if (v1 === 0xfe && v2 === 0xb0) {
      r.skip(1);
      stitches.push({ x, y, cmd: CMD.COLOR_CHANGE });
      continue;
    }
    let jump = false, trim = false;
    let dx: number;
    let next = v2;
    if (v1 & 0x80) {
      if (v1 & TRIM_CODE) trim = true;
      if (v1 & JUMP_CODE) jump = true;
      dx = signed12((v1 << 8) | v2);
      next = r.u8();
    } else {
      dx = signed7(v1);
    }
    let dy: number;
    if (next & 0x80) {
      if (next & TRIM_CODE) trim = true;
      if (next & JUMP_CODE) jump = true;
      dy = signed12((next << 8) | r.u8());
    } else {
      dy = signed7(next);
    }
    x += dx;
    y += dy;
    if (trim) {
      stitches.push({ x: x - dx, y: y - dy, cmd: CMD.TRIM });
      stitches.push({ x, y, cmd: CMD.JUMP });
    } else if (jump) {
      stitches.push({ x, y, cmd: CMD.JUMP });
    } else {
      stitches.push({ x, y, cmd: CMD.STITCH });
    }
  }
  if (!ended) throw new FormatError(t('The PEC stitch block is missing the end code (0xFF).'));
  stitches.push({ x, y, cmd: CMD.END });
  return {
    label,
    colorIndices,
    width,
    height,
    stitches,
    graphicsOffset: blockStart + blockLength,
    iconCount: Math.floor((bytes.length - (blockStart + blockLength)) / (ICON_STRIDE * ICON_H)),
  };
}

export function readPec(bytes: Uint8Array): PecSection {
  const sig = String.fromCharCode(...bytes.subarray(0, 8));
  if (sig !== '#PEC0001') throw new FormatError(t('Not a PEC file.'));
  return readPecSection(bytes, 8);
}
